import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNotice } from '../shared/NoticeProvider';
import { TaxonomyLabelReview } from '../signals-live/TaxonomyLabelReview';
import SignalsControlsBar from './SignalsControlsBar';
import ConductFeedSkeleton from './feed/ConductFeedSkeleton';
import SignalsTableHeader from './feed/SignalsTableHeader';
import QuickFiltersBar from './feed/QuickFiltersBar';
import PaginationBar from './feed/PaginationBar';
// WebSocket handled in useConductFeed
import { useConductFeed } from './hooks/useConductFeed';
import FiltersModal, { FiltersModalValues } from '../signals-live/FiltersModal';
import SignalCreationHub from '../signals/SignalCreationHub';
import SignalsList from './feed/SignalsList';

const ConductIntelligenceFeed: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [feedbackFor, setFeedbackFor] = useState<string | null>(null);
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  const { show: showNotice } = useNotice();

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<FiltersModalValues>({
    severityMin: 0,
    timeRange: 'none'
  });

  // Column visibility state - load from localStorage
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('conductFeedColumns');
    if (saved) {
      try {
        const cols = JSON.parse(saved);
        return new Set(cols);
      } catch (e) {
        // Default: all columns visible except taxonomyVersion
        return new Set(['severity', 'date', 'customer', 'summary', 'complaint', 'vulnerability']);
      }
    }
    // Default: all columns visible except taxonomyVersion
    return new Set(['severity', 'date', 'customer', 'summary', 'complaint', 'vulnerability']);
  });

  // Save column visibility to localStorage
  const toggleColumn = (columnName: string) => {
    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnName)) {
        newSet.delete(columnName);
      } else {
        newSet.add(columnName);
      }
      localStorage.setItem('conductFeedColumns', JSON.stringify(Array.from(newSet)));
      return newSet;
    });
  };

  // Hook: centralize feed state and actions
  const feed = useConductFeed({
    filterValues,
    searchParams,
    setSearchParams,
    paginationLimit: 20,
    showNotice,
  });
  const {
    signals,
    loading,
    error,
    detailCache,
    expanded,
    threadOpen,
    selected,
    pagination: hookPagination,
    deleting,
    progressTick,
    searchTerm,
    setSearchTerm,
    quickFilters,
    debugExpandUrl,
    debugTooltip,
    fetchSignals,
    handleReclassifySignal,
    handleBulkReclassify,
    handleLabelTap,
    toggleExpand,
    toggleThread,
    ensureDetail,
    toggleSelect,
    toggleSelectAll,
    removeQuickFilter,
    clearAllFilters,
    deleteSelected,
    allVisibleSelected,
    handleSort,
    sortBy,
    sortOrder,
    justUpdated,
    datasetIds,
    handleDatasetChange,
  } = feed;
  const hasActiveFilters = useMemo(() => filterValues.severityMin !== 0 || filterValues.timeRange !== 'none', [filterValues]);
  // Column width tokens used by both header and rows to keep alignment
  const col = useMemo(() => ({
    select: 'w-10 mr-2',
    severity: 'w-20 mr-6',
    date: 'w-28 mr-6',
    customer: 'w-40 mr-6',
    summary: 'flex-1 mr-6',
    taxonomyVersion: 'w-24 mr-6',
    complaint: 'w-28 mr-6',
    vulnerability: 'w-32 mr-6',
    chevron: 'w-6'
  }), []);

  // WebSocket handled inside useConductFeed

  // fetchSignals provided by hook

  // selection + deletion handled by hook

  // Read URL params on mount
  useEffect(() => {
    const severityMin = searchParams.get('severityMin');
    const dateFrom = searchParams.get('dateFrom');
    const dateTo = searchParams.get('dateTo');
    const urlSearch = searchParams.get('search') || '';

    if (severityMin || dateFrom || dateTo) {
      setFilterValues({
        severityMin: severityMin ? parseInt(severityMin, 10) : 0,
        timeRange: dateFrom || dateTo ? 'custom' : 'none',
        from: dateFrom || undefined,
        to: dateTo || undefined
      });
    }
    setSearchTerm(urlSearch);
    // Dataset filtering is now handled automatically by useConductFeed hook via datasetId URL parameter

    // Quick filters from URL
    const tax = (searchParams.get('taxonomyCategoryKeys') || '').split(',').map(s=>s.trim()).filter(Boolean);
    if (tax.length) {
      // Use URL params setter to ensure hook picks this up on first fetch
      const newParams = new URLSearchParams(searchParams);
      if (tax.length) newParams.set('taxonomyCategoryKeys', tax.join(','));
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, setSearchTerm]);

  // Sync filters to URL
  const syncFiltersToUrl = (values: FiltersModalValues) => {
    const newParams = new URLSearchParams(searchParams);

    if (values.severityMin !== 0) {
      newParams.set('severityMin', values.severityMin.toString());
    } else {
      newParams.delete('severityMin');
    }

    if (values.timeRange === 'custom' && values.from) {
      newParams.set('dateFrom', values.from);
    } else {
      newParams.delete('dateFrom');
    }

    if (values.timeRange === 'custom' && values.to) {
      newParams.set('dateTo', values.to);
    } else {
      newParams.delete('dateTo');
    }

    // Sync quick filters
    const taxKeys = quickFilters.map(f => f.key);
    if (taxKeys.length) newParams.set('taxonomyCategoryKeys', taxKeys.join(',')); else newParams.delete('taxonomyCategoryKeys');

    setSearchParams(newParams);
  };

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const listScrollRef = useRef<HTMLDivElement | null>(null);

  // Removed local fades logic; encapsulated in SignalsControlsBar to avoid rerenders

  // Search debounce handled in hook

  // Progress tick handled in hook

  // Kebab menu handled inside SignalsControlsBar

  // Fades are managed internally by SignalsControlsBar

  // Fetch lifecycle handled in hook

  // Toggle handlers provided by hook
  const openFeedback = async (id: string) => {
    await ensureDetail(id);
    setFeedbackFor(id);
  };

  // Label tap handled by hook's handleLabelTap

  // Quick filter remove/clear handled by hook

  // debugExpandUrl and debugTooltip provided by hook

  // Reclassify handler is provided by hook

  // handleSort provided by hook

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // removed getTimeAgo; handled in child components


  if (loading && signals.length === 0) {
    return <ConductFeedSkeleton col={col} />;
  }

  if (error) {
    return (
      <div className="p-6 text-red-600">{error}</div>
    );
  }

  return (
    <>
    <div className="flex flex-col h-full bg-white -mx-6 -mt-4">
      {/* Controls bar with padding: memoized to avoid re-rendering on list updates */}
      <div className="px-6">
        <SignalsControlsBar
          onOpenFilters={() => setShowFilters(true)}
          hasActiveFilters={hasActiveFilters}
          searchTerm={searchTerm}
          onSearchTermChange={(val) => setSearchTerm(val)}
          selectedSize={selected.size}
          deleting={deleting}
          onDeleteSelected={deleteSelected}
          onReclassifySelected={handleBulkReclassify}
          searchInputRef={searchInputRef}
          searchFocused={searchFocused}
          setSearchFocused={setSearchFocused}
          visibleColumns={visibleColumns}
          onToggleColumn={toggleColumn}
          datasetIds={feed.datasetIds}
          onDatasetChange={feed.handleDatasetChange}
        />
      </div>

      {/* Inline notifications */}
      {/* Global notices handled by NoticeProvider */}

      <QuickFiltersBar
        quickFilters={quickFilters}
        onRemove={removeQuickFilter}
        onClearAll={clearAllFilters}
        debugUrl={debugExpandUrl}
        debugTooltip={debugTooltip}
        datasetIds={feed.datasetIds}
        onDatasetClear={() => feed.handleDatasetChange([])}
      />

      {/* Table header and list - full width with no horizontal padding */}
      <SignalsTableHeader
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        allSelected={allVisibleSelected}
        onToggleAll={toggleSelectAll}
        col={col}
        visibleColumns={visibleColumns}
      />

      <SignalsList
        signals={signals}
        detailCache={detailCache}
        expanded={expanded}
        threadOpen={threadOpen}
        selectedIds={selected}
        col={col}
        visibleColumns={visibleColumns}
        formatDate={formatDate}
        handleReclassifySignal={handleReclassifySignal}
        handleLabelTap={handleLabelTap}
        openFeedback={openFeedback}
        justUpdated={justUpdated}
        listScrollRef={listScrollRef}
        progressTick={progressTick}
        onToggleExpand={toggleExpand}
        onToggleThread={toggleThread}
        onToggleSelect={toggleSelect}
      />

      {/* Signal Creation Hub Modal (importer + create) */}
      <SignalCreationHub
        isOpen={isCreationModalOpen}
        onClose={() => setIsCreationModalOpen(false)}
        onSignalCreated={() => {
          // Refresh to reflect new signal(s)
          void fetchSignals(1);
        }}
        onImportComplete={() => {
          // Imported signals stream in via websocket; refresh list as well
          void fetchSignals(1);
        }}
      />

      <PaginationBar
        page={hookPagination.page}
        limit={hookPagination.limit}
        total={hookPagination.total || signals.length}
        hasPrev={hookPagination.hasPrev}
        hasNext={hookPagination.hasNext}
        onPrev={() => void fetchSignals(hookPagination.page - 1)}
        onNext={() => void fetchSignals(hookPagination.page + 1)}
      />
    </div>
    {feedbackFor && (() => {
      const detail = detailCache[feedbackFor];
      if (!detail?.classification?.id || !detail?.taxonomy_classification) return null;
      return (
        <TaxonomyLabelReview
          classification_id={detail.classification.id}
          taxonomy_classification={detail.taxonomy_classification}
          onClose={() => setFeedbackFor(null)}
          signal_id={detail.id}
          signalSummary={detail.summary_detailed || detail.summary}
          signalVerbatim={detail.verbatim}
          customerName={detail.customer_name || detail.customer?.name}
          conductScore={detail.conduct_score ?? detail.classification?.conduct_score}
        />
      );
    })()}

    <FiltersModal
      open={showFilters}
      initial={filterValues}
      onApply={(values) => {
        setFilterValues(values);
        syncFiltersToUrl(values);
        setShowFilters(false);
        void fetchSignals(1);
      }}
      onClose={() => setShowFilters(false)}
    />

  </>
  );
};

export default ConductIntelligenceFeed;
